import { LoadLayoutStore } from "@core/presentacion/component/feedback/load-layout/load-layout.store";
import { BaseViewModel } from "@core/presentacion/view-model/base/base.viewmodel";
import type {
    PublicApiUsageStats,
    PublicApiUsageStatsByApiKeyRow,
    PublicApiUsageStatsTopScope,
} from "@entity-api-keys/domain/models/public-api-usage-stats.model";
import { ExportPublicApiUsageStatsXlsxUseCase } from "@entity-api-keys/domain/usecases/export-public-api-usage-stats-xlsx.usecase";
import { GetPublicApiUsageStatsUseCase } from "@entity-api-keys/domain/usecases/get-public-api-usage-stats.usecase";
import { inject, injectable } from "inversify";
import {
    action,
    computed,
    makeObservable,
    observable,
    runInAction,
} from "mobx";

export interface PublicApiUsageStatsTableRow {
    key: string;
    apiKeyName: string;
    apiKeyPrefix: string;
    entityName: string;
    requests: number;
    errors: number;
    topScopes: PublicApiUsageStatsTopScope[];
}

export interface PublicApiUsageStatsScopeChartDatum {
    name: string;
    value: number;
}

@injectable()
export class PublicApiUsageStatsPageViewModel extends BaseViewModel {
    @observable loading: boolean = true;

    @observable period: "24h" | "7d" | "30d" = "7d";

    @observable stats: PublicApiUsageStats = {
        period: "7d",
        from: "",
        to: "",
        totals: { requests: 0, errors: 0 },
        by_api_key: [],
    };

    constructor(
        @inject(GetPublicApiUsageStatsUseCase)
        private readonly getStatsUseCase: GetPublicApiUsageStatsUseCase,

        @inject(ExportPublicApiUsageStatsXlsxUseCase)
        private readonly exportXlsxUseCase: ExportPublicApiUsageStatsXlsxUseCase,
    ) {
        super();
        makeObservable(this);
    }

    override async didMount(): Promise<void> {
        await super.didMount();
        await this.refresh();
    }

    @computed
    get errorRate(): number {
        if (this.stats.totals.requests === 0) return 0;
        return (this.stats.totals.errors / this.stats.totals.requests) * 100;
    }

    @computed
    get tableRows(): PublicApiUsageStatsTableRow[] {
        return (this.stats.by_api_key ?? []).map(
            (row: PublicApiUsageStatsByApiKeyRow) => {
                const apiKeyId = row.api_key?.id;
                const key =
                    apiKeyId !== null && apiKeyId !== undefined
                        ? String(apiKeyId)
                        : row.api_key?.name || "invalid";

                return {
                    key,
                    apiKeyName: row.api_key?.name ?? "",
                    apiKeyPrefix: row.api_key?.prefix ?? "",
                    entityName: row.api_key?.entity_name ?? "",
                    requests: row.requests ?? 0,
                    errors: row.errors ?? 0,
                    topScopes: row.top_scopes ?? [],
                };
            },
        );
    }

    @computed
    get topScopesChartData(): PublicApiUsageStatsScopeChartDatum[] {
        const scopes = this.stats.top_scopes ?? [];
        return scopes
            .slice()
            .sort((a, b) => (b.requests ?? 0) - (a.requests ?? 0))
            .slice(0, 10)
            .map((s) => ({ name: s.scope, value: s.requests ?? 0 }));
    }

    @action
    setPeriod(period: "24h" | "7d" | "30d"): void {
        this.period = period;
        this.refresh();
    }

    async refresh(): Promise<void> {
        runInAction(() => {
            this.loading = true;
        });

        const stats = await this.getStatsUseCase.execute(this.period);

        runInAction(() => {
            this.stats = stats;
            this.loading = false;
        });
    }

    async exportXlsx(): Promise<void> {
        LoadLayoutStore.start();

        try {
            const blob = await this.exportXlsxUseCase.execute(this.period);

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
                "download",
                `public-api-usage-stats-${this.period}.xlsx`,
            );
            document.body.appendChild(link);
            link.click();
        } catch {
            // errors are handled via ToastManagerStore in the use case
        } finally {
            LoadLayoutStore.finish();
        }
    }
}
